import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { PiCaretDown, PiMagicWand } from 'react-icons/pi';
import { ChipMenu, TOOLTIP_PLACEMENT_LEFT, Tooltip } from '@aibuild/ui';
import type { CapturedView } from '../../viewerSettings';
import type { ViewRenderCapability, ViewRenderOffer } from '../../viewRender';
import { usePortalContainer } from '../../environment';
import { useChromeLayout } from '../ChromeContext';
import { RENDER_MAX_EDGE_PX } from '../chrome.constants';
import { messages } from '../messages';
import { usePopover } from '../usePopover';
import { themeLabel } from './themes';
import {
  Chip,
  Chips,
  ErrorText,
  ModelField,
  ModelTrigger,
  Panel,
  RenderAction,
  RenderSlot,
  SectionLabel,
  WandButton,
} from './AiRender.styled';

export interface AiRenderProps {
  capability: ViewRenderCapability;
  captureView: (maxEdge: number) => CapturedView | null;
  onResult: (image: string) => void;
}

type OfferState = { status: 'loading' } | { status: 'ready'; offer: ViewRenderOffer } | { status: 'failed' };

/** The AI render button, and the panel that turns the view on screen into a picture. */
const AiRender: React.FC<AiRenderProps> = ({ capability, captureView, onResult }) => {
  const intl = useIntl();
  const portal = usePortalContainer();
  const { narrow } = useChromeLayout();
  const visitor = capability.requireAccount !== undefined;
  const [offer, setOffer] = useState<OfferState>({ status: 'loading' });
  const [model, setModel] = useState<string | null>(null);
  const [theme, setTheme] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const wandRef = useRef<HTMLButtonElement>(null);
  const modelsRef = useRef<HTMLButtonElement>(null);
  const panel = usePopover(wandRef, panelRef);
  const models = usePopover(modelsRef);
  const pending = useRef<AbortController | null>(null);
  const closePanel = panel.close;

  useEffect(() => {
    if (visitor) return undefined;
    let current = true;
    capability.loadOffer().then(
      (loaded) => {
        if (!current) return;
        if (!loaded.available) closePanel();
        setOffer({ status: 'ready', offer: loaded });
        setModel(loaded.defaultModel ?? loaded.models[0]?.id ?? null);
        setTheme(loaded.defaultTheme ?? loaded.themes[0] ?? null);
      },
      () => {
        if (current) setOffer({ status: 'failed' });
      },
    );
    return () => {
      current = false;
    };
  }, [capability, visitor, closePanel]);

  useEffect(() => () => pending.current?.abort(), []);

  if (offer.status === 'ready' && !offer.offer.available) return null;

  const label = intl.formatMessage(messages.aiRender);
  const ready = offer.status === 'ready' ? offer.offer : null;
  const chosen = ready?.models.find((option) => option.id === model) ?? null;
  const nameOfTheme = (id: string) => {
    const descriptor = themeLabel(id);
    return descriptor ? intl.formatMessage(descriptor) : id;
  };

  const render = async () => {
    if (!chosen || !theme || rendering) return;
    setError(null);
    const view = captureView(RENDER_MAX_EDGE_PX);
    if (!view) {
      setError(intl.formatMessage(messages.aiCaptureFailed));
      return;
    }
    const controller = new AbortController();
    pending.current = controller;
    setRendering(true);
    try {
      const result = await capability.render(
        {
          view: view.dataUrl,
          width: view.width,
          height: view.height,
          background: view.background,
          model: chosen.id,
          theme,
        },
        controller.signal,
      );
      if (controller.signal.aborted) return;
      panel.close();
      onResult(result.image);
    } catch (failure) {
      if (controller.signal.aborted) return;
      const message = failure instanceof Error && failure.message ? failure.message : null;
      setError(message ?? intl.formatMessage(messages.aiRenderFailed));
    } finally {
      if (pending.current === controller) pending.current = null;
      if (!controller.signal.aborted) setRendering(false);
    }
  };

  const actionLabel = () => {
    if (rendering) return intl.formatMessage(messages.aiRendering);
    if (!chosen) return intl.formatMessage(messages.aiRenderPending);
    return intl.formatMessage(messages.aiRenderAction, {
      credits: intl.formatNumber(chosen.credits, { maximumFractionDigits: 2 }),
    });
  };

  return (
    <RenderSlot>
      <Tooltip
        content={label}
        placement={TOOLTIP_PLACEMENT_LEFT}
        open={panel.tooltipOpen}
        portal
        portalContainer={portal}
      >
        <WandButton
          ref={wandRef}
          type="button"
          aria-label={label}
          aria-expanded={visitor ? undefined : panel.open}
          $open={panel.open}
          $narrow={narrow}
          onClick={visitor ? capability.requireAccount : panel.toggle}
          data-testid="model-viewer__ai-render"
        >
          <PiMagicWand />
        </WandButton>
      </Tooltip>
      {panel.open && (
        <Panel ref={panelRef} $narrow={narrow} role="dialog" aria-label={label} data-testid="model-viewer__ai-panel">
          {offer.status === 'failed' && <ErrorText role="alert">{intl.formatMessage(messages.aiOfferFailed)}</ErrorText>}
          <ModelField>
            <SectionLabel>{intl.formatMessage(messages.aiModel)}</SectionLabel>
            <ModelTrigger
              ref={modelsRef}
              type="button"
              aria-haspopup="menu"
              aria-expanded={models.open}
              disabled={!ready || rendering}
              onClick={models.toggle}
              onMouseDown={models.keepOpenOnPress}
              data-testid="model-viewer__ai-model"
            >
              <span>{chosen?.displayName ?? '–'}</span>
              <PiCaretDown />
            </ModelTrigger>
            {models.open && ready && (
              <ChipMenu
                onClose={models.close}
                width="100%"
                ariaLabel={intl.formatMessage(messages.aiModel)}
                dataTestId="model-viewer__ai-model-menu"
              >
                <ChipMenu.List>
                  {ready.models.map((option) => (
                    <ChipMenu.Item
                      key={option.id}
                      isActive={option.id === model}
                      meta={intl.formatNumber(option.credits, { maximumFractionDigits: 2 })}
                      title={option.description}
                      onClick={() => {
                        setModel(option.id);
                        models.close();
                      }}
                      dataTestId={`model-viewer__ai-model-${option.id}`}
                    >
                      {option.displayName}
                    </ChipMenu.Item>
                  ))}
                </ChipMenu.List>
              </ChipMenu>
            )}
          </ModelField>
          {ready && ready.themes.length > 0 && (
            <>
              <SectionLabel>{intl.formatMessage(messages.aiTheme)}</SectionLabel>
              <Chips role="group" aria-label={intl.formatMessage(messages.aiTheme)}>
                {ready.themes.map((option) => (
                  <Chip
                    key={option}
                    type="button"
                    $active={option === theme}
                    aria-pressed={option === theme}
                    disabled={rendering}
                    onClick={() => setTheme(option)}
                    data-testid={`model-viewer__ai-theme-${option}`}
                  >
                    {nameOfTheme(option)}
                  </Chip>
                ))}
              </Chips>
            </>
          )}
          <RenderAction
            type="button"
            disabled={!chosen || !theme || rendering}
            onClick={() => void render()}
            data-testid="model-viewer__ai-render-action"
          >
            {actionLabel()}
          </RenderAction>
          {error && <ErrorText role="alert">{error}</ErrorText>}
        </Panel>
      )}
    </RenderSlot>
  );
};

export default AiRender;
